import { DragEvent, FunctionComponent } from 'react';
import {
  File,
  FileExcelSolid,
  FileImageSolid,
  FilePdfSolid,
  FilePowerPointSolid,
  FileVideoSolid,
  FileWordSolid,
} from '@assets/svgs';
import {
  MAX_BYTES_SIZE,
  IMAGE_TYPES,
  VIDEO_TYPES,
  DOCUMENT_TYPES,
  MAX_MB_SIZE,
} from '@components/drawerItem/DrawerItem.constants';
import { PostArtifact, MediaType } from '@models/note';

const getFileType = (fileType: string) => {
  const media = fileType.split('.');
  return media[media.length - 1];
};

const getMediaType = (fileType: string) => {
  // using document as default value
  let mediaType = MediaType.Document;
  const media = fileType.split('/');
  const type = media[1];
  if (IMAGE_TYPES.includes(type)) {
    mediaType = MediaType.Image;
  } else if (VIDEO_TYPES.includes(type)) {
    mediaType = MediaType.Video;
  } else if (DOCUMENT_TYPES.includes(type)) {
    mediaType = MediaType.Document;
  }
  return mediaType;
};

const isDataTransfer = (e: DragEvent) => {
  if (e.dataTransfer.types) {
    for (let i = 0; i < e.dataTransfer.types.length; i += 1) {
      if (e.dataTransfer.types[i] === 'Files') {
        return true;
      }
    }
  }
  return false;
};

function instanceOfINewArtifact(data: any): data is PostArtifact {
  return 'file' in data;
}

// Function to sanitize the filename
const sanitizeFileName = (fileName: string): string => {
  const invalidChars = /[^a-zA-Z0-9. _-]/g;
  return fileName.replace(invalidChars, '');
};

const createArtifact = (f: File, invalidMsg: string | null) => {
  const fileType = f.type;

  const artifact: PostArtifact = {
    description: '',
    type: getMediaType(fileType),
    mimetype: fileType,
    file: f,
    name: sanitizeFileName(f.name),
    size: f.size,
    invalidMsg,
  };
  return artifact;
};

const getPostArtifact = (file: File) => {
  const selectedFiles: PostArtifact[] = [];
  // const fileType = getFileType(file.name);
  let invalidMsg = null;
  if (file.size > MAX_BYTES_SIZE) {
    invalidMsg = `Max file size exceeded (>${MAX_MB_SIZE}MB)`;
  } else if (!acceptedFileMimeTypes.includes(file.type) || (file.type === '' && file.name.split('.')[1] !== 'msg')) {
    invalidMsg = 'Unsupported file type';
  }
  const newArtifact = createArtifact(file, invalidMsg);
  selectedFiles.push(newArtifact);
  return selectedFiles;
};

function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 KB';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

const acceptedFileMimeTypes = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'video/mp4',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  'text/csv',
  'text/plain',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.ms-outlook',
  '',
];

const iconsByMimeType: Record<string, FunctionComponent> = {
  default: File,
  'application/pdf': FilePdfSolid,
  'image/png': FileImageSolid,
  'image/jpeg': FileImageSolid,
  'image/gif': FileImageSolid,
  'video/mp4': FileVideoSolid,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': FileExcelSolid,
  'application/vnd.ms-excel': FileExcelSolid,
  'application/vnd.ms-excel.sheet.macroEnabled.12': FileExcelSolid,
  'text/csv': FileExcelSolid,
  'text/plain': File,
  'application/vnd.ms-powerpoint': FilePowerPointSolid,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': FilePowerPointSolid,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': FileWordSolid,
  'application/msword': FileWordSolid,
  'application/vnd.ms-outlook': File,
  '': File,
};

const getIconForMimeType = (mimeType?: string | null): FunctionComponent =>
  iconsByMimeType[mimeType ?? 'default'] ?? iconsByMimeType.default;

export {
  acceptedFileMimeTypes,
  iconsByMimeType,
  getFileType,
  getMediaType,
  isDataTransfer,
  instanceOfINewArtifact,
  getPostArtifact,
  createArtifact,
  formatBytes,
  getIconForMimeType,
};
